import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { RouteComponentProps } from 'react-router-dom';
import catchOnly from 'utils/catchOnly';

import * as Yup from 'yup';
import {
  composeSchema,
  isPhoneNumber,
  isSsn,
  isZipCode,
} from 'utils/validations';

import FmcForm from 'components/FmcForm';
import LookupFormView from '../../LookupFormView';
import { FormValues } from '../PhoneValidation';

import useOTPSender, { OTPSenderError } from 'hooks/requests/otpSender';

import { NonClientError } from 'hooks/requests/fetchUtils';
import useNavigationHelpers from 'hooks/useNavigationHelpers';

import { mergeApplication } from 'state/creditApplication';

import useFieldCustomization from 'hooks/useFieldCustomization';

type PropTypes = RouteComponentProps & {
  onSubmit: (values: FormValues) => void;
  values: FormValues;
  showSsn: boolean;
  showZip: boolean;
};

const LookupForm: React.FC<PropTypes> = props => {
  const { isLastFour } = useFieldCustomization();
  const isLastFourBool = isLastFour();
  const [sendOTP] = useOTPSender();
  const {
    onSubmit: onSubmitSetValues,
    match,
    values,
    showSsn,
    showZip,
  } = props;

  const { push } = useNavigationHelpers();
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const schema = useMemo(
    () =>
      Yup.object().shape({
        mobilePhone: composeSchema(
          Yup.string().label('Mobile #'),
          isPhoneNumber,
          Yup.string()
            .required()
            .default(''),
        ),

        ...(showSsn
          ? {
              ssn: Yup.string()
                .label(isLastFourBool ? 'Last 4 SSN' : 'SSN')
                .concat(isSsn(isLastFourBool))
                .required()
                .default(''),
            }
          : {}),

        ...(showZip
          ? {
              zip: composeSchema(
                Yup.string().label('ZIP Code'),
                Yup.string()
                  .required()
                  .default(''),
                isZipCode,
              ),
            }
          : {}),
      }),
    [showSsn, showZip, isLastFourBool],
  );

  const onSubmit = useCallback(
    async (rawValues: FormValues) => {
      const values = schema.cast(rawValues);

      dispatch(
        mergeApplication({
          mobilePhone: values.mobilePhone,
          ...(showSsn ? { ssn: values.ssn } : {}),
          ...(showZip ? { zip: values.zip } : {}),
        }),
      );

      try {
        await sendOTP({ phone: values.mobilePhone as string });
        onSubmitSetValues(values);
        push(`${match.path}/otp-verification`.replace('//', '/'));
      } catch (e) {
        if (catchOnly(e, NonClientError, OTPSenderError)) {
          enqueueSnackbar('Something has gone wrong. Please try again later.', {
            variant: 'error',
            persist: true,
          });
        }
      }
    },
    [
      showSsn,
      showZip,
      enqueueSnackbar,
      push,
      sendOTP,
      schema,
      dispatch,
      onSubmitSetValues,
      match,
    ],
  );

  const initialValues = {
    mobilePhone: values.mobilePhone,
    ...(showSsn ? { ssn: values.ssn } : {}),
    ...(showZip ? { zip: values.zip } : {}),
  };

  return (
    <FmcForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {formProps => <LookupFormView {...formProps} />}
    </FmcForm>
  );
};

export default LookupForm;
